import { ForwardedRef, Key, forwardRef, useId, useMemo } from "react";

import {
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  Autocomplete as MUIAutocomplete,
  MenuItem,
} from "@mui/material";
import { gray } from "configs/colors";
import { rem } from "utils";

import { useMultiAutocomplete } from "./MultiAutocomplete.hooks";
import {
  Container,
  Input,
  SelectedIconWrapper,
  StyledPopper,
  Tag,
} from "./MultiAutocomplete.styles";
import {
  MultiAutocompleteProps,
  MultiAutocompleteValue,
  MultiAutocompleteReference,
} from "./MultiAutocomplete.types";
import { CheckIcon, ChevronDownIcon, XCloseIcon, XIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";
import Typography from "../Typography/Typography";

const MultiAutocompleteInner = <V, R>(
  {
    color,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    inputRef,
    label,
    loading,
    options = [],
    placeholder,
    prefix,
    readOnly,
    required,
    suffix,
    value,
    tooltipText,
    limitTags,
    disabledLocalSearch = false,
    groupBy,
    renderOption,
    renderTag,
    onChange,
    onInputChange,
    onFocus,
    onLoadMore,
  }: MultiAutocompleteProps<MultiAutocompleteValue<V>>,
  ref: ForwardedRef<R>
) => {
  const { autocompleteRef, valueState, onChangeHandler } = useMultiAutocomplete(
    { ref, defaultValue, value, onChange }
  );
  const formId = useId();

  // MUI requires options sorted by group, otherwise group headers are duplicated
  const sortedOptions = useMemo(() => {
    if (!groupBy) return options;
    const groups = new Map<string, typeof options>();
    options.forEach((option) => {
      const key = groupBy(option);
      groups.set(key, [...(groups.get(key) ?? []), option]);
    });
    return Array.from(groups.values()).flat();
  }, [options, groupBy]);

  return (
    <Container>
      {label && (
        <InputLabel
          htmlFor={id ?? formId}
          required={required}
          helperText={tooltipText}
        >
          {label}
        </InputLabel>
      )}
      <MUIAutocomplete
        multiple
        fullWidth
        disablePortal={false}
        disableCloseOnSelect
        ref={autocompleteRef}
        id={id ?? formId}
        limitTags={limitTags}
        color={color}
        disabled={disabled}
        loading={loading}
        readOnly={readOnly}
        options={sortedOptions}
        groupBy={groupBy}
        value={valueState}
        popupIcon={<ChevronDownIcon fontSize="small" />}
        clearIcon={<XIcon fontSize="small" />}
        isOptionEqualToValue={(option, v) => option.value === v.value}
        onChange={(_event, selectedValue) =>
          onChangeHandler(
            selectedValue as unknown as MultiAutocompleteValue<V>[] | null
          )
        }
        getOptionDisabled={(option) => option.value === "DISABLED"}
        onInputChange={(_e, v, r) => onInputChange?.(v, r)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFocus={onFocus as any}
        ListboxProps={{
          onScroll: (event) => {
            const listboxNode = event.currentTarget;
            const bottomThreshold = 10;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - bottomThreshold
            ) {
              if (onLoadMore) {
                onLoadMore();
              }
            }
          },
        }}
        renderOption={(props, option, state) => (
          <MenuItem {...props}>
            {typeof renderOption === "function"
              ? renderOption(props, option, state)
              : option.label}
            {state.selected ? (
              <SelectedIconWrapper>
                <CheckIcon color="primary" fontSize="small" />
              </SelectedIconWrapper>
            ) : null}
          </MenuItem>
        )}
        renderInput={(params) => (
          <Input
            {...params}
            color={color}
            inputRef={inputRef}
            required={required}
            disabled={disabled}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            error={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {prefix ? (
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      marginX={rem(6)}
                    >
                      {prefix}
                    </Box>
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={rem(14)} />
                  ) : null}
                  {suffix}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(v, getTagProps) =>
          v.map((o, index: number) => {
            const { key, className, onDelete, ...innerProps } = getTagProps({
              index,
            });
            return (
              <Tag
                key={key as unknown as Key}
                className="MultiAutocomplete-Tag"
                {...innerProps}
              >
                {typeof renderTag === "function" ? (
                  renderTag(o)
                ) : (
                  <Typography
                    fontWeight={500}
                    color="inherit"
                    sx={{ fontSize: rem(11), lineHeight: rem(16) }}
                  >
                    {o.label}
                  </Typography>
                )}
                {!innerProps.disabled ? (
                  <IconButton
                    disableRipple
                    sx={{
                      padding: rem(2),
                      cursor: "pointer",
                      color: gray[400],
                      marginLeft: rem(3),
                      flex: "0 0 auto",
                    }}
                    onClick={onDelete}
                  >
                    <XCloseIcon
                      color="inherit"
                      sx={{
                        fontSize: rem(12),
                      }}
                    />
                  </IconButton>
                ) : null}
              </Tag>
            );
          })
        }
        PopperComponent={StyledPopper}
        {...(disabledLocalSearch && { filterOptions: (opts) => opts })}
      />
      {helperText && (
        <FormHelperText disabled={disabled} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Container>
  );
};

const MultiAutocomplete = forwardRef(MultiAutocompleteInner) as <V, D>(
  props: MultiAutocompleteProps<MultiAutocompleteValue<V, D>> & {
    ref?: ForwardedRef<
      MultiAutocompleteReference<MultiAutocompleteValue<V, D>>
    >;
  }
) => ReturnType<typeof MultiAutocompleteInner>;

export default MultiAutocomplete;
